import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApiResponse } from "@shared/types/api";

export type SocialPlatform = "instagram" | "facebook";

export interface SocialCrawlPage {
  id: string;
  platform: SocialPlatform;
  pageIdentifier: string;
  displayName: string | null;
  groupId: string | null;
  groupName: string | null;
  groupSlug: string | null;
  lastCrawledAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  eventCount: number;
}

export interface CrawlGroup {
  id: string;
  name: string;
  slug: string;
  imageUrl: string | null;
}

export interface CrawledPostPreview {
  id: string;
  platform: SocialPlatform;
  text: string;
  imageUrls: string[];
  imageDataUrl: string | null;
  postUrl: string;
  postedAt: string | null;
}

export interface ResolvedVenue {
  name: string;
  address: string;
  lat: number | null;
  lng: number | null;
  placeId: string | null;
  cityId: string | null;
  cityName: string | null;
}

export interface ExtractedEventPreview {
  isEvent: boolean;
  confidence: number;
  name: string;
  date: string | null;
  time: string | null;
  endTime: string | null;
  venueName: string | null;
  venueAddress: string | null;
  description: string;
  types: string[];
  priceCents: number | null;
  coverImageUrl: string | null;
  coverImageDataUrl: string | null;
  links: Array<{ type: string; url: string }>;
  sourcePostUrl: string;
  resolvedVenue: ResolvedVenue | null;
  existingEventId: string | null;
  existingEventName: string | null;
}

export interface DebugCrawlResult {
  mapsConfigured: boolean;
  posts: CrawledPostPreview[];
  extractedEvents: ExtractedEventPreview[];
}

export interface CrawlRunResult {
  pageId: string;
  platform: string;
  pageIdentifier: string;
  postsFound: number;
  discardedNotEvent: number;
  discardedLowConfidence: number;
  discardedMissingDate: number;
  discardedMissingTime: number;
  eligibleEvents: number;
  venueFromCache: number;
  venueFromMaps: number;
  venueNoMapsResult: number;
  venueNoCityMatch: number;
  coverImageUploaded: number;
  coverImageFailed: number;
  eventsSaved: number;
  eventsSaveFailed: number;
  savedEventSlugs: string[];
  promptTokens: number;
  candidateTokens: number;
  totalTokens: number;
  error?: string;
}

export interface CrawlParams {
  maxPosts?: number;
  sinceDate?: string | null; // ISO string or null (all-time)
}

export interface SaveDebugEventBody {
  name: string;
  date: string;
  time: string;
  endTime?: string | null;
  description?: string;
  types: string[];
  priceCents?: number | null;
  coverImageUrl?: string | null;
  links: Array<{ type: string; url: string }>;
  sourcePostUrl: string;
  groupId?: string | null;
  resolvedVenue: ResolvedVenue;
}

type PagesResponse = ApiResponse<SocialCrawlPage[]>;
type GroupsResponse = ApiResponse<CrawlGroup[]>;
type DebugResponse = ApiResponse<DebugCrawlResult>;
type CrawlResponse = ApiResponse<CrawlRunResult>;
type SaveDebugResponse = ApiResponse<{ eventId: string; slug: string }>;

// ──────────────────────────────────────────────────────────────────────────────

export function useAdminSocialCrawlPages() {
  return useQuery<PagesResponse>({
    queryKey: ["admin-social-crawl-pages"],
    queryFn: (): Promise<PagesResponse> => api.get("/admin/social-crawling/pages"),
  });
}

export function useAddSocialCrawlPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      platform: SocialPlatform;
      pageIdentifier: string;
      displayName?: string;
      groupId?: string;
    }): Promise<PagesResponse> => api.post("/admin/social-crawling/pages", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-social-crawl-pages"] }),
  });
}

export interface BatchAddResult {
  added: number;
  skipped: number;
  pages: SocialCrawlPage[];
}

export function useAddSocialCrawlPagesBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      platform: SocialPlatform;
      pageIdentifiers: string[];
      groupId?: string;
    }): Promise<ApiResponse<BatchAddResult>> => api.post("/admin/social-crawling/pages/batch", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-social-crawl-pages"] }),
  });
}

export function useUpdateSocialCrawlPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      displayName?: string;
      isActive?: boolean;
      groupId?: string | null;
    }): Promise<PagesResponse> => api.patch(`/admin/social-crawling/pages/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-social-crawl-pages"] }),
  });
}

export function useAdminCrawlGroups() {
  return useQuery<GroupsResponse>({
    queryKey: ["admin-crawl-groups"],
    queryFn: (): Promise<GroupsResponse> => api.get("/admin/social-crawling/groups"),
  });
}

export function useDeleteSocialCrawlPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<ApiResponse<void>> =>
      api.delete(`/admin/social-crawling/pages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-social-crawl-pages"] }),
  });
}

export function useDebugSocialCrawl() {
  return useMutation<
    DebugResponse,
    Error,
    {
      platform: SocialPlatform;
      pageIdentifier: string;
      maxPosts: number;
      maxPostAgeDays: number;
      extractEvents: boolean;
      resolveVenues: boolean;
    }
  >({
    mutationFn: (body): Promise<DebugResponse> =>
      api.post("/admin/social-crawling/debug", body),
  });
}

export function useRunSocialCrawl() {
  const qc = useQueryClient();
  return useMutation<CrawlResponse, Error, { pageId: string; params: CrawlParams }>({
    mutationFn: ({ pageId, params }): Promise<CrawlResponse> =>
      api.post(`/admin/social-crawling/pages/${pageId}/crawl`, params),
    onSuccess: (res, { pageId }) => {
      const saved = res.data?.eventsSaved ?? 0;
      // Patch in-place — no full refetch so list order stays stable.
      qc.setQueryData<ApiResponse<SocialCrawlPage[]>>(
        ["admin-social-crawl-pages"],
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((p) =>
              p.id === pageId
                ? { ...p, lastCrawledAt: new Date().toISOString(), eventCount: p.eventCount + saved }
                : p,
            ),
          };
        },
      );
    },
  });
}

export function useSaveDebugEvent() {
  return useMutation<SaveDebugResponse, Error, SaveDebugEventBody>({
    mutationFn: (body): Promise<SaveDebugResponse> =>
      api.post("/admin/social-crawling/debug/save", body),
  });
}

export function usePageCrawledEvents(pageId: string | null) {
  return useQuery({
    queryKey: ["page-crawled-events", pageId],
    queryFn: () => api.get(`/admin/social-crawling/pages/${pageId}/events`),
    enabled: !!pageId,
  });
}
